import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProductById, getAllProducts, getAllProductsSync } from '../utils/productService';
import { pageOptions } from '../data/products';
import { storeImagesForOrder } from '../utils/imageStorage';
import { convertHeicFileToJpeg, convertHeicFilesToJpeg, isUnsupportedRawFile } from '../lib/heicConvert';
import Header from '../components/Header';
import SiteFooter from '../components/SiteFooter';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { localizeProduct } from '../utils/localizeProduct';
import './ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, isInCart } = useCart();
  const { t, lang } = useLanguage();

  const [product, setProduct] = useState(getAllProductsSync().find(p => p.id === parseInt(id)) || null);
  const [allProducts, setAllProducts] = useState(getAllProductsSync());
  const [selectedPages, setSelectedPages] = useState(pageOptions[0]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [productImages, setProductImages] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    getProductById(id).then(p => { if (p) setProduct(p); });
    getAllProducts().then(setAllProducts);
  }, [id]);

  const loadProductImages = (product) => {
    if (product.imageUrls && product.imageUrls.length > 0) {
      return product.imageUrls;
    }
    const images = [];
    if (product.imageFolder && product.imageCount) {
      for (let i = 1; i <= product.imageCount; i++) {
        try {
          const img = require(`../assets/${product.imageFolder}/img${i}.webp`);
          images.push(img);
        } catch (error) {
          console.error(`Image not found: ${product.imageFolder}/img${i}.webp`);
        }
      }
    } else if (product.mainImage) {
      try {
        const img = require(`../assets/${product.mainImage}`);
        images.push(img);
      } catch (error) {
        console.error(`Image not found: ${product.mainImage}`);
      }
    }
    return images;
  };

  useEffect(() => {
    if (product) {
      const images = loadProductImages(product);
      setProductImages(images);
      setCurrentImageIndex(0);
    }
    window.scrollTo(0, 0);
  }, [product]);

  if (!product) {
    return (
      <div className="product-detail-page">
        <Header />
        <div className="not-found">{t('product.loading')}</div>
      </div>
    );
  }

  let totalPrice, originalPrice, discountPercent;
  if (product.category === 'frame') {
    totalPrice = product.price || product.totalPrice || 7.99;
    originalPrice = product.originalPrice || null;
    discountPercent = null;
  } else if (product.category === 'kids') {
    totalPrice = product.price || product.totalPrice || 14.90;
    originalPrice = product.originalPrice || null;
    discountPercent = null;
  } else if (product.category === 'kitchen' || product.category === 'agenda') {
    totalPrice = product.price || product.totalPrice || 12.90;
    originalPrice = product.originalPrice || null;
    discountPercent = null;
  } else {
    totalPrice = selectedPages.price || 24.90;
    originalPrice = null;
    discountPercent = null;
  }

  const handleFramePreview = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (isUnsupportedRawFile(file)) {
        alert(t('product.alert.rawFormat', { name: file.name }));
        return;
      }
      const converted = await convertHeicFileToJpeg(file);
      const imageUrl = URL.createObjectURL(converted);
      setPreviewImage(imageUrl);
      if (product.category === 'frame') {
        setUploadedImages([{ url: imageUrl, file: converted, name: converted.name }]);
      }
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const imageExtensions = /\.(jpg|jpeg|jpe|jfif|pjpeg|png|gif|bmp|webp|avif|svg|tiff|tif|ico|heic|heif|raw|cr2|cr3|crw|nef|arw|srf|dng|orf|raf|rw2)$/i;
    const invalidFiles = files.filter(file => !file.type.startsWith('image/') && !imageExtensions.test(file.name));
    if (invalidFiles.length > 0) {
      alert(t('product.alert.notPhotos', { names: invalidFiles.map(f => f.name).join(', ') }));
      const validFiles = files.filter(file => file.type.startsWith('image/') || imageExtensions.test(file.name));
      if (validFiles.length === 0) return;
      handleValidFiles(validFiles);
      return;
    }
    handleValidFiles(files);
  };

  const handleValidFiles = async (files) => {
    const convertedFiles = await convertHeicFilesToJpeg(files);
    if (product.id !== 10 && selectedPages.maxPhotos) {
      const currentCount = uploadedImages.length;
      const newCount = currentCount + convertedFiles.length;
      const maxAllowed = selectedPages.maxPhotos;
      if (newCount > maxAllowed) {
        const remaining = maxAllowed - currentCount;
        if (remaining <= 0) {
          alert(t('product.alert.maxReached', { max: maxAllowed, label: t(selectedPages.labelKey) }));
          return;
        } else {
          alert(t('product.alert.remainingOnly', { remaining, label: t(selectedPages.labelKey), max: maxAllowed }));
          const allowedFiles = convertedFiles.slice(0, remaining);
          const imageUrls = allowedFiles.map(file => ({ url: URL.createObjectURL(file), file, name: file.name }));
          setUploadedImages([...uploadedImages, ...imageUrls]);
          return;
        }
      }
    }
    const imageUrls = convertedFiles.map(file => ({ url: URL.createObjectURL(file), file, name: file.name }));
    setUploadedImages([...uploadedImages, ...imageUrls]);
  };

  const removeImage = (index) => {
    setUploadedImages(uploadedImages.filter((_, i) => i !== index));
  };

  const handleKidsImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/') && !file.name.toLowerCase().endsWith('.heic') && !file.name.toLowerCase().endsWith('.heif')) {
      alert(t('product.alert.onlyPhotos'));
      return;
    }
    if (isUnsupportedRawFile(file)) {
      alert(t('product.alert.rawFormat', { name: file.name }));
      return;
    }
    const converted = await convertHeicFileToJpeg(file);
    setUploadedImages([{ url: URL.createObjectURL(converted), file: converted, name: converted.name }]);
  };

  const handleOrder = () => {
    const metadata = { product, totalPrice };
    if (product.category !== 'frame' && product.category !== 'kids') metadata.selectedPages = selectedPages;
    storeImagesForOrder(uploadedImages, metadata);
    navigate('/order', { state: { hasStoredOrder: true } });
  };

  const localizedProduct = localizeProduct(product, lang);

  return (
    <div className="product-detail-page">
      <Header />
      <div className="detail-container">

        {/* ── Main Product Card ── */}
        <div className="pd-card">
          <div className="product-detail-grid">

            {/* LEFT: Image showcase */}
            <div className="product-showcase">
              {/* Vertical thumbnails */}
              {productImages.length > 1 && (
                <div className="thumbnails-col">
                  {productImages.map((img, index) => (
                    <div
                      key={index}
                      className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                      onClick={() => setCurrentImageIndex(index)}
                    >
                      <img src={img} alt={`${localizedProduct.name} ${index + 1}`} />
                    </div>
                  ))}
                  {productImages.length > 4 && (
                    <div className="thumb-more-btn">∨</div>
                  )}
                </div>
              )}

              {/* Main image */}
              <div className={`showcase-image ${product.category === 'frame' ? 'frame-preview-container' : ''}`}>
                {product.category === 'frame' && previewImage ? (
                  <div className="frame-with-photo">
                    <img src={previewImage} alt="Preview në kornizë" className="frame-photo-preview" />
                  </div>
                ) : productImages.length > 0 ? (
                  <>
                    <img
                      src={productImages[currentImageIndex]}
                      alt={localizedProduct.name}
                      className="main-product-image"
                    />
                    <button className="zoom-btn" aria-label="Zoom">
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                    </button>
                    {productImages.length > 1 && (
                      <>
                        <div className="image-navigation">
                          <button className="nav-btn prev-btn"
                            onClick={() => setCurrentImageIndex(prev => prev === 0 ? productImages.length - 1 : prev - 1)}>
                            ‹
                          </button>
                          <button className="nav-btn next-btn"
                            onClick={() => setCurrentImageIndex(prev => prev === productImages.length - 1 ? 0 : prev + 1)}>
                            ›
                          </button>
                        </div>
                        <div className="image-dots">
                          {productImages.map((_, i) => (
                            <span key={i} className={`dot ${i === currentImageIndex ? 'active' : ''}`}
                              onClick={() => setCurrentImageIndex(i)} />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="showcase-icon">📖</div>
                )}
              </div>
            </div>

            {/* RIGHT: Product details */}
            <div className="product-details">
              <span className="product-category-label">{localizedProduct.name}</span>
              <h1 className="detail-title">{localizedProduct.name}</h1>
              <p className="detail-description">{localizedProduct.fullDescription}</p>

              {/* Features + Price row */}
              <div className="features-price-row">
                <div className="features-list">
                  <h3>{t('product.features')}</h3>
                  <ul>
                    {localizedProduct.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </div>
                <div className="price-box">
                  <span className="price-label">{t('product.totalPrice')}</span>
                  <span className="price-value">€{totalPrice.toFixed(2)}</span>
                  {originalPrice && originalPrice > totalPrice && (
                    <span className="original-price-strike">{originalPrice.toFixed(2)} €</span>
                  )}
                </div>
              </div>

              <button
                className="add-to-cart-btn"
                onClick={() => addToCart({ id: product.id, name: localizedProduct.name, price: totalPrice, image: productImages[0] || product.imageUrls?.[0] })}
                disabled={isInCart(product.id)}
              >
                {isInCart(product.id) ? t('product.inCart') : t('product.addToCart')}
              </button>

              {/* Page Selection */}
              {product.category !== 'frame' && product.category !== 'kids' && product.category !== 'kitchen' && product.category !== 'agenda' && (
                <div className="page-selection-inline">
                  <h3 className="page-selection-title">{t('product.choosePhotos')}</h3>
                  <div className="page-options-inline">
                    {pageOptions.map((option) => (
                      <button
                        key={option.pages}
                        className={`page-option-btn ${selectedPages.pages === option.pages ? 'active' : ''}`}
                        onClick={() => setSelectedPages(option)}
                      >
                        <div className="option-radio-circle">
                          {selectedPages.pages === option.pages && <div className="option-radio-dot" />}
                        </div>
                        <div className="option-texts">
                          <span className="option-label">{t(option.labelKey)}</span>
                          {option.price > 0 && <span className="option-extra">+{option.price}€</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Kitchen */}
              {product.category === 'kitchen' && (
                <>
                  <p className="kitchen-personalization-note">
                    {t('product.kitchenNote')}
                  </p>
                  <button className="design-btn" onClick={() => {
                    storeImagesForOrder([], { product, totalPrice });
                    navigate('/order', { state: { hasStoredOrder: true } });
                  }}>
                    {t('product.order')}
                  </button>
                </>
              )}

              {/* Agenda */}
              {product.category === 'agenda' && (
                <button className="design-btn" onClick={() => {
                  storeImagesForOrder([], { product, totalPrice });
                  navigate('/order', { state: { hasStoredOrder: true } });
                }}>
                  {t('product.order')}
                </button>
              )}

              {/* Design button for normal books */}
              {product.category !== 'frame' && product.category !== 'kids' && product.category !== 'kitchen' && product.category !== 'agenda' && (
                <>
                  <button className="design-btn" onClick={() => {
                    sessionStorage.setItem('pb_new_session', '1');
                    navigate(`/design/${product.id}`, { state: { ...product, displayName: localizedProduct.name, selectedPages, totalPrice } });
                  }}>
                    {t('product.designOnline')} <span className="btn-arrow">→</span>
                  </button>
                  <div className="security-text">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                    {t('product.security')}
                  </div>
                </>
              )}

              {/* Frame upload */}
              {product.category === 'frame' && (
                <div className="upload-section">
                  <h3 className="option-title">{t('product.tryFramePhoto')}</h3>
                  <div className="frame-upload-section">
                    <label className="frame-upload-btn">
                      <input type="file" accept="image/*" onChange={handleFramePreview} style={{ display: 'none' }} />
                      {t('product.uploadFramePhoto')}
                    </label>
                    <p className="upload-hint">{t('product.frameHint')}</p>
                  </div>
                  <button className="design-btn" style={{ marginTop: '20px' }} onClick={handleOrder}>
                    {t('home.orderNow')}
                  </button>
                </div>
              )}

              {/* Kids book upload */}
              {product.category === 'kids' && (
                <div className="upload-section">
                  <h3 className="option-title">{t('product.uploadPhoto')}</h3>
                  <div className="frame-upload-section">
                    <label className="frame-upload-btn">
                      <input type="file" accept="image/*" onChange={handleKidsImageUpload} style={{ display: 'none' }} />
                      {t('product.uploadPhoto')}
                    </label>
                    <p className="upload-hint">{t('product.kidsHint')}</p>
                  </div>
                  {uploadedImages.length > 0 && (
                    <div className="uploaded-images" style={{ marginTop: '16px' }}>
                      <div className="images-grid">
                        {uploadedImages.map((img, index) => (
                          <div key={index} className="image-preview">
                            <img src={img.url} alt={`Upload ${index + 1}`} />
                            <button className="remove-image-btn" onClick={() => removeImage(index)}>✕</button>
                            <div className="image-name">{img.name}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <button className="design-btn" style={{ marginTop: '20px' }} onClick={handleOrder}>
                    {t('home.orderNow')}
                  </button>
                </div>
              )}

              {/* Frame uploaded images preview */}
              {product.category === 'frame' && uploadedImages.length > 0 && (
                <div className="uploaded-images" style={{ marginTop: '16px' }}>
                  <h4>{t('product.uploadedPhotos', { n: uploadedImages.length })}</h4>
                  <div className="images-grid">
                    {uploadedImages.map((img, index) => (
                      <div key={index} className="image-preview">
                        <img src={img.url} alt={`Upload ${index + 1}`} />
                        <button className="remove-image-btn" onClick={() => removeImage(index)}>✕</button>
                        <div className="image-name">{img.name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Related Products Section ── */}
        <div className="customization-section">
          <h2 className="section-heading">{t('product.customizeTitle')}</h2>
          <div className="option-group">
            <h3 className="option-title">{t('product.seeOurProducts')}</h3>
            <div className="book-store-grid">
              {allProducts.map((rawBookProduct) => {
                const bookProduct = localizeProduct(rawBookProduct, lang);
                let firstImage = null;
                if (bookProduct.imageUrls && bookProduct.imageUrls.length > 0) {
                  firstImage = bookProduct.imageUrls[0];
                } else if (bookProduct.imageFolder) {
                  try { firstImage = require(`../assets/${bookProduct.imageFolder}/img1.webp`); } catch (e) {}
                } else if (bookProduct.mainImage) {
                  try { firstImage = require(`../assets/${bookProduct.mainImage}`); } catch (e) {}
                }
                const hasDiscount = bookProduct.originalPrice && bookProduct.originalPrice > bookProduct.price;
                const dPct = hasDiscount ? (bookProduct.category === 'frame' ? 60 : 15) : 0;
                return (
                  <div key={bookProduct.id} className="book-card" onClick={() => navigate(`/product/${bookProduct.id}`)}>
                    <div className="book-image-container">
                      {hasDiscount && (
                        <div className="book-discount-badge">{t('product.promoPrice')}</div>
                      )}
                      {firstImage ? (
                        <img src={firstImage} alt={bookProduct.name} className="book-image" />
                      ) : (
                        <div className="book-placeholder">📚</div>
                      )}
                    </div>
                    <div className="book-info">
                      <h4 className="book-title">{bookProduct.name}</h4>
                      <p className="book-subtitle">{bookProduct.shortDescription}</p>
                      <div className="book-pricing">
                        <div className="book-current-price">€{Number.isInteger(bookProduct.price) ? <>{bookProduct.price}<sup>99</sup></> : bookProduct.price.toFixed(2)}</div>
                        {hasDiscount && (
                          <>
                            <span className="book-original-price">{Number.isInteger(bookProduct.originalPrice) ? `${bookProduct.originalPrice}.00` : bookProduct.originalPrice.toFixed(2)} €</span>
                            <span className="book-discount-label">-{dPct}%</span>
                          </>
                        )}
                      </div>
                      <button className="book-details-btn">{t('product.seeDetails')}</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="option-group" />
        </div>
      </div>

      <SiteFooter />
    </div>
  );
};

export default ProductDetail;
